import axios from '@/config/axios';
import { useToast } from 'vue-toastification';

function unwrap(res) {
  const { status, message, result } = res.data;
  if (status !== '200') throw new Error(message || 'Có lỗi xảy ra từ máy chủ.');
  return result;
}

export const approvalApi = {
  getPendingApprovals: (type, page = 0) => {
    return axios.get('approval/v2/get-list', { params: { type, page } }).then(unwrap);
  },
  getPendingCounts: () => {
    return axios.get('approval/v2/get-pending-counts').then(unwrap);
  },
  getApprovalHistory: (type, status = 'ALL', page = 0) => {
    return axios.get('approval/v2/get-history', { params: { type, status, page } }).then(unwrap);
  },
  approveDelete: (payload) => {
    return axios.post('approval/v2/approve', payload).then(unwrap);
  },
  rejectDelete: (payload) => {
    return axios.post('approval/v2/reject', payload).then(unwrap);
  },
  getHistory: (type, id) => {
    return axios.get(`history/v2/${type}/get-by-id/${id}`).then(res => {
      const { status, message, result } = res.data;
      if (status === '404') return [];
      if (status !== '200') throw new Error(message || 'Có lỗi xảy ra từ máy chủ.');
      return result;
    });
  },
  getBookById: (id) => {
    return axios.get(`book/v1/detail/${id}`).then(unwrap);
  }
};

const toast = {
  success(msg, options = {}) { return useToast().success(msg, { position: "top-center", ...options }); },
  error(msg, options = {}) { return useToast().error(msg, { position: "top-center", ...options }); }
};

export default {
  name: 'AdminApprovals',
  data() {
    return {
      mainView: 'pending', // 'pending' | 'history'
      activeTab: 'book',
      items: [],
      pendingCounts: {
        book: 0,
        author: 0,
        translator: 0,
        category: 0
      },
      isLoading: false,
      isProcessing: false,
      approveModalVisible: false,
      rejectModalVisible: false,
      viewModalVisible: false,
      historyModalVisible: false,
      isHistoryLoading: false,
      selectedItem: null,
      viewItem: null,
      selectedBook: null,
      imageLoadError: false,
      imagePreviewVisible: false,
      selectedItemForHistory: null,
      historyList: [],
      rejectReason: '',
      errors: { rejectReason: '' },
      isAdmin: false,

      // History Tab States
      historyItems: [],
      historyStatusFilter: 'ALL', // 'ALL' | 'APPROVED' | 'REJECTED' | 'PENDING'
      historySearchQuery: '',
      historyPage: 0,
      historyTotalPages: 1,
      historyTotalElements: 0,
      isHistoryTableLoading: false,
      historySearchTimeout: null
    };
  },
  computed: {
    entityLabel() {
      if (this.activeTab === 'book') return 'Sách';
      if (this.activeTab === 'author') return 'Tác giả';
      if (this.activeTab === 'translator') return 'Dịch giả';
      if (this.activeTab === 'category') return 'Thể loại';
      return '';
    },
    pageTitle() {
      if (this.mainView === 'pending') {
        return this.isAdmin ? 'Phê duyệt yêu cầu' : 'Yêu cầu chờ duyệt của tôi';
      }
      return this.isAdmin ? 'Lịch sử xét duyệt' : 'Lịch sử đề xuất của tôi';
    },
    pageSubtitle() {
      if (this.mainView === 'pending') {
        return this.isAdmin
          ? 'Quản lý và xét duyệt các yêu cầu xóa/khôi phục từ nhân viên'
          : 'Danh sách các yêu cầu xóa/khôi phục bạn đã gửi đang chờ Quản trị viên duyệt';
      }
      return this.isAdmin
        ? 'Xem lại toàn bộ các yêu cầu bạn đã phê duyệt hoặc từ chối'
        : 'Theo dõi trạng thái và kết quả các đề xuất (Đã duyệt / Bị từ chối) của bạn';
    },
    emptyStateText() {
      if (this.mainView === 'pending') {
        return this.isAdmin
          ? `Đang không có ${this.entityLabel.toLowerCase()} nào chờ duyệt`
          : `Bạn chưa có yêu cầu nào cho ${this.entityLabel.toLowerCase()} đang chờ duyệt`;
      }
      return `Không có dữ liệu lịch sử xét duyệt ${this.entityLabel.toLowerCase()} nào phù hợp`;
    },
    filteredHistoryItems() {
      if (!this.historySearchQuery.trim()) {
        return this.historyItems;
      }
      const q = this.historySearchQuery.trim().toLowerCase();
      return this.historyItems.filter(item => {
        const name = (item.name || '').toLowerCase();
        const reqReason = (item.requestReason || '').toLowerCase();
        const procReason = (item.processReason || '').toLowerCase();
        const reqBy = (item.requestByName || '').toLowerCase();
        const procBy = (item.processByName || '').toLowerCase();
        const action = (item.actionLabel || '').toLowerCase();
        return name.includes(q) || reqReason.includes(q) || procReason.includes(q) || reqBy.includes(q) || procBy.includes(q) || action.includes(q);
      });
    }
  },
  watch: {
    activeTab() {
      if (this.mainView === 'pending') {
        this.fetchData();
      } else {
        this.fetchHistoryData(0);
      }
    }
  },
  mounted() {
    this.checkUserRole();
    this.fetchPendingCounts();
    this.fetchData();
  },
  methods: {
    checkUserRole() {
      const raw = localStorage.getItem('current-account');
      if (raw) {
        try {
          const acc = JSON.parse(raw);
          let roles = acc.authority;
          if (typeof roles === 'string') {
            try { roles = JSON.parse(roles); } catch (e) { roles = [roles]; }
          }
          if (Array.isArray(roles)) {
            this.isAdmin = roles.some(r => {
              const name = String(r.authority || r).toUpperCase();
              return name === 'ROLE_ADMIN' || name === 'ADMIN';
            });
          } else {
            const name = String(roles).toUpperCase();
            this.isAdmin = name === 'ROLE_ADMIN' || name === 'ADMIN';
          }
        } catch (e) {
          this.isAdmin = false;
        }
      }
    },
    switchMainView(view) {
      if (this.mainView === view) return;
      this.mainView = view;
      if (view === 'pending') {
        this.fetchPendingCounts();
        this.fetchData();
      } else {
        this.fetchHistoryData(0);
      }
    },
    fetchPendingCounts() {
      approvalApi.getPendingCounts()
        .then((counts) => {
          if (counts) {
            this.pendingCounts = {
              book: Number(counts.book) || 0,
              author: Number(counts.author) || 0,
              translator: Number(counts.translator) || 0,
              category: Number(counts.category) || 0
            };
          }
        })
        .catch(() => {
          // Silent catch
        });
    },
    fetchData() {
      this.isLoading = true;
      this.items = [];
      this.fetchPendingCounts();

      approvalApi.getPendingApprovals(this.activeTab, 0)
        .then((res) => {
          let list = res.content || res || [];
          this.items = list.map(item => ({
            ...item,
            requestTime: item.updateTime || item.update_time || item.createTime || item.create_time || ''
          }));
          if (this.pendingCounts[this.activeTab] !== undefined) {
            this.pendingCounts[this.activeTab] = this.items.length;
          }
        })
        .catch(err => {
          const msg = err.message || '';
          if (msg.includes('Không có') || msg.includes('không tìm thấy') || msg.includes('not found') || msg.includes('Chưa có') || msg.includes('404')) {
            this.items = [];
            if (this.pendingCounts[this.activeTab] !== undefined) {
              this.pendingCounts[this.activeTab] = 0;
            }
          } else {
            toast.error(err.message || 'Lỗi khi lấy dữ liệu');
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    fetchHistoryData(page = 0) {
      this.isHistoryTableLoading = true;
      this.historyPage = page;
      this.historyItems = [];

      approvalApi.getApprovalHistory(this.activeTab, this.historyStatusFilter, page)
        .then(res => {
          if (res && res.content) {
            this.historyItems = res.content || [];
            this.historyTotalPages = res.totalPages || 1;
            this.historyTotalElements = res.totalElements || 0;
          } else if (Array.isArray(res)) {
            this.historyItems = res;
            this.historyTotalPages = 1;
            this.historyTotalElements = res.length;
          } else {
            this.historyItems = [];
            this.historyTotalPages = 1;
            this.historyTotalElements = 0;
          }
        })
        .catch(err => {
          const msg = err.message || '';
          if (msg.includes('Không có') || msg.includes('không tìm thấy') || msg.includes('not found') || msg.includes('Chưa có') || msg.includes('404')) {
            this.historyItems = [];
          } else {
            toast.error(err.message || 'Lỗi khi lấy dữ liệu lịch sử xét duyệt');
          }
        })
        .finally(() => {
          this.isHistoryTableLoading = false;
        });
    },

    changeHistoryStatusFilter(status) {
      this.historyStatusFilter = status;
      this.fetchHistoryData(0);
    },

    onSearchHistoryInput(val) {
      this.historySearchQuery = val;
    },

    formatVersionTime(version) {
      if (!version) return 'N/A';
      const str = String(version).trim();
      if (/^\d{14}$/.test(str)) {
        const y = str.substring(0, 4);
        const m = str.substring(4, 6);
        const d = str.substring(6, 8);
        const h = str.substring(8, 10);
        const min = str.substring(10, 12);
        const sec = str.substring(12, 14);
        return `${h}:${min}:${sec} ${d}/${m}/${y}`;
      }
      if (/^\d{12}$/.test(str)) {
        const y = str.substring(0, 4);
        const m = str.substring(4, 6);
        const d = str.substring(6, 8);
        const h = str.substring(8, 10);
        const min = str.substring(10, 12);
        return `${h}:${min}:00 ${d}/${m}/${y}`;
      }
      if (/^\d{2}:\d{2}\s+\d{2}\/\d{2}\/\d{4}$/.test(str)) {
        const parts = str.split(/\s+/);
        return `${parts[0]}:00 ${parts[1]}`;
      }
      return str;
    },

    formatCoverUrl(cover) {
      if (!cover) return '';
      const str = String(cover).trim();
      if (str.startsWith('data:') || str.startsWith('http://') || str.startsWith('https://')) {
        return str;
      }
      return 'data:image/png;base64,' + str;
    },

    handleImageError(e) {
      if (e && e.target) {
        e.target.style.display = 'none';
        if (e.target.nextElementSibling) {
          e.target.nextElementSibling.style.display = 'flex';
        }
      }
    },

    getReason(item) {
      return item.reason || 'Không có lý do';
    },

    getApprovalBadgeClass(status) {
      if (status === 'APPROVED') return 'badge-approval-approved';
      if (status === 'REJECTED') return 'badge-approval-rejected';
      return 'badge-approval-pending';
    },

    getApprovalBadgeIcon(status) {
      if (status === 'APPROVED') return 'fa-solid fa-circle-check';
      if (status === 'REJECTED') return 'fa-solid fa-circle-xmark';
      return 'fa-solid fa-clock';
    },

    getActionBadgeClass(action) {
      if (action === 'RESTORE') return 'badge-action-restore';
      return 'badge-action-delete';
    },

    isRestoreItem(item) {
      if (!item) return false;
      if (item.status === 3 || item.actionType === 'RESTORE') return true;
      const r = (item.reason || item.requestReason || '').toLowerCase();
      return r.includes('khôi phục');
    },

    getCleanReason(item) {
      if (!item) return '';
      let r = item.reason || item.requestReason || '';
      if (r.startsWith('Yêu cầu khôi phục:')) {
        return r.replace(/^Yêu cầu khôi phục:\s*/, '');
      }
      if (r.startsWith('Yêu cầu xóa:')) {
        return r.replace(/^Yêu cầu xóa:\s*/, '');
      }
      return r;
    },

    confirmApprove(item) {
      this.selectedItem = item;
      this.approveModalVisible = true;
    },

    confirmReject(item) {
      this.selectedItem = item;
      this.rejectReason = '';
      this.errors.rejectReason = '';
      this.rejectModalVisible = true;
    },

    openViewModal(item) {
      let resolvedStatus = item.status !== undefined ? item.status : item.currentStatus;
      if (resolvedStatus === undefined || resolvedStatus === null) {
        if (item.approvalStatus === 'APPROVED' && item.actionType === 'DELETE') {
          resolvedStatus = 2; // Đã xóa
        } else if (item.approvalStatus === 'APPROVED' && item.actionType === 'RESTORE') {
          resolvedStatus = 0; // Phát hành
        } else if (item.approvalStatus === 'PENDING') {
          resolvedStatus = 1; // Chờ xóa
        } else {
          resolvedStatus = 0;
        }
      }

      this.selectedBook = {
        ...item,
        status: resolvedStatus,
        cover: item.cover || ''
      };
      this.viewItem = this.selectedBook;
      this.viewModalVisible = true;
      this.imageLoadError = false;

      if (item && item.id && (!this.selectedBook.authors || !this.selectedBook.authors.length)) {
        approvalApi.getBookById(item.id)
          .then(book => {
            if (book && this.viewModalVisible && this.selectedBook?.id === item.id) {
              const finalCover = this.selectedBook.cover || book.cover || '';
              this.selectedBook = {
                ...this.selectedBook,
                ...book,
                cover: finalCover,
                authors: this.selectedBook.authors && this.selectedBook.authors.length ? this.selectedBook.authors : (book.authors || []),
                translators: this.selectedBook.translators && this.selectedBook.translators.length ? this.selectedBook.translators : (book.translators || []),
                categories: this.selectedBook.categories && this.selectedBook.categories.length ? this.selectedBook.categories : (book.categories || []),
                evaluate: this.selectedBook.evaluate !== undefined ? this.selectedBook.evaluate : (book.evaluate !== undefined ? book.evaluate : 0),
                status: this.selectedBook.status !== undefined ? this.selectedBook.status : (book.status !== undefined ? book.status : resolvedStatus),
                link_shopee: this.selectedBook.link_shopee || this.selectedBook.linkShopee || book.link_shopee || book.linkShopee,
                content: this.selectedBook.content || book.content,
                reason: this.selectedBook.reason || this.selectedBook.requestReason,
                requestReason: this.selectedBook.requestReason || this.selectedBook.reason,
                processReason: this.selectedBook.processReason
              };
              this.viewItem = this.selectedBook;
              if (finalCover) {
                this.imageLoadError = false;
              }
            }
          })
          .catch(() => {});
      }
    },

    closeViewModal() {
      this.viewModalVisible = false;
      this.viewItem = null;
      this.selectedBook = null;
    },

    openImagePreview() {
      this.imagePreviewVisible = true;
    },

    closeImagePreview() {
      this.imagePreviewVisible = false;
    },

    closeModals() {
      this.approveModalVisible = false;
      this.rejectModalVisible = false;
      this.viewModalVisible = false;
      this.historyModalVisible = false;
      this.imagePreviewVisible = false;
      this.selectedItem = null;
      this.selectedBook = null;
      this.selectedItemForHistory = null;
    },

    openHistoryModal(item) {
      this.selectedItemForHistory = item;
      this.historyModalVisible = true;
      this.isHistoryLoading = true;
      this.historyList = [];

      approvalApi.getHistory(this.activeTab, item.id)
        .then(res => {
          this.historyList = res || [];
        })
        .catch(err => {
          toast.error(err.message || 'Có lỗi xảy ra khi tải lịch sử.');
        })
        .finally(() => {
          this.isHistoryLoading = false;
        });
    },

    closeHistoryModal() {
      this.historyModalVisible = false;
      this.selectedItemForHistory = null;
      this.historyList = [];
    },

    getHistoryActionLabel(type) {
      if (type === 1 || type === 'Khởi tạo') return 'Khởi tạo';
      if (type === 2 || type === 'Cập nhật' || type === 'Cập nhật thông tin') return 'Cập nhật';
      if (type === 3 || type === 'Yêu cầu xóa' || type === 'Xóa bỏ') return 'Yêu cầu xóa';
      return typeof type === 'string' ? type : 'Không xác định';
    },

    getHistoryBadgeClass(type) {
      if (type === 1 || type === 'Khởi tạo') return 'badge-init';
      if (type === 2 || type === 'Cập nhật' || type === 'Cập nhật thông tin') return 'badge-update';
      if (type === 3 || type === 'Yêu cầu xóa' || type === 'Xóa bỏ') return 'badge-danger';
      return 'badge-status';
    },

    getHistoryIcon(type) {
      if (type === 1 || type === 'Khởi tạo') return 'fa-solid fa-plus';
      if (type === 2 || type === 'Cập nhật' || type === 'Cập nhật thông tin') return 'fa-solid fa-pen';
      if (type === 3 || type === 'Yêu cầu xóa' || type === 'Xóa bỏ') return 'fa-solid fa-trash';
      return 'fa-solid fa-circle-info';
    },

    submitApprove() {
      if (!this.selectedItem || this.isProcessing) return;
      this.isProcessing = true;

      const isRestore = this.isRestoreItem(this.selectedItem);
      const actionName = isRestore ? 'khôi phục' : 'xóa';

      const payload = {
        type: this.activeTab,
        id: this.selectedItem.id,
        updateTime: this.selectedItem.updateTime || this.selectedItem.update_time || ''
      };

      approvalApi.approveDelete(payload)
        .then(() => {
          toast.success(`Đã phê duyệt ${actionName} ${this.entityLabel.toLowerCase()} thành công!`);
          this.closeModals();
          this.fetchData();
        })
        .catch(err => {
          toast.error(err.message || 'Có lỗi xảy ra khi phê duyệt.');
        })
        .finally(() => {
          this.isProcessing = false;
        });
    },

    submitReject() {
      if (!this.selectedItem || this.isProcessing) return;
      
      const reasonVal = (this.rejectReason || '').trim();
      if (!reasonVal) {
        this.errors.rejectReason = 'Vui lòng nhập lý do từ chối.';
        return;
      }
      if (reasonVal.length < 10) {
        this.errors.rejectReason = 'Lý do phải có ít nhất 10 ký tự.';
        return;
      }
      if (reasonVal.length > 500) {
        this.errors.rejectReason = 'Lý do không được quá 500 ký tự.';
        return;
      }

      this.errors.rejectReason = '';
      this.isProcessing = true;

      const isRestore = this.isRestoreItem(this.selectedItem);
      const actionName = isRestore ? 'khôi phục' : 'xóa';

      const payload = {
        type: this.activeTab,
        id: this.selectedItem.id,
        reason: reasonVal,
        updateTime: this.selectedItem.updateTime || this.selectedItem.update_time || ''
      };

      approvalApi.rejectDelete(payload)
        .then(() => {
          toast.success(`Đã từ chối yêu cầu ${actionName} ${this.entityLabel.toLowerCase()} thành công!`);
          this.closeModals();
          this.fetchData();
        })
        .catch(err => {
          toast.error(err.message || 'Có lỗi xảy ra khi từ chối.');
        })
        .finally(() => {
          this.isProcessing = false;
        });
    },

    handleImageError(event) {
      if (event && event.target) {
        event.target.style.display = 'none';
        if (event.target.nextElementSibling) {
          event.target.nextElementSibling.style.display = 'flex';
        }
      }
    }
  }
};
